import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Space, Button, Input, Select, Tag, Descriptions, Drawer, Typography, message } from 'antd';
import { ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { apiService } from '../../../services/apiService';

interface ProductRow {
  id: number;
  productId: string;
  title?: string;
  vendor?: string;
  productType?: string;
  status?: string;
  tags?: string | null;
  createdAt?: string;
  updatedAt?: string;
  variantsCount?: number;
}

const ShopifyProduct: React.FC = () => {
  const [data, setData] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [filters, setFilters] = useState({ q: '', status: undefined as string | undefined, vendor: '' });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.vendor) params.vendor = filters.vendor;
      const resp: any = await apiService.getShopifyProducts(params);
      if (resp?.success) {
        setData(resp.data || []);
      } else if (Array.isArray(resp)) {
        setData(resp);
      } else {
        setData([]);
      }
    } catch (e) {
      message.error('獲取 Shopify 產品失敗');
    } finally {
      setLoading(false);
    }
  }, [filters.q, filters.status, filters.vendor]);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchProducts, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const list = useMemo(() => (data||[]).map(p => ({
    key: p.id,
    title: p.title,
    vendor: p.vendor,
    productType: p.productType,
    status: p.status,
    createdAt: p.createdAt,
    variantsCount: p.variantsCount,
    raw: p,
  })), [data]);

  const columns: any[] = [
    { title: '名稱', dataIndex: 'title' },
    { title: '品牌', dataIndex: 'vendor', width: 160 },
    { title: '類型', dataIndex: 'productType', width: 160 },
    { title: '狀態', dataIndex: 'status', width: 120, render: (s: string)=> <Tag color={s==='active'?'green':'default'}>{s||'-'}</Tag> },
    { title: '建立時間', dataIndex: 'createdAt', width: 180 },
    { title: '變體數', dataIndex: 'variantsCount', width: 100 },
  ];

  const handleExport = () => {
    try {
      if (!data?.length) { message.warning('沒有數據可導出'); return; }
      const headers = ['名稱','品牌','類型','狀態','建立時間','變體數'];
      const rows = data.map(p => [
          p.title||'',
          p.vendor||'',
          p.productType||'',
          p.status||'',
          p.createdAt ? `\u200B${new Date(p.createdAt).toISOString().split('T')[0]}` : '',
          String(p.variantsCount||0)
      ]);
      const csvContent = [headers, ...rows].map(r=>r.map(x=>String(x).replace(/"/g,'""')).map(x=>/[",\n]/.test(x)?`"${x}"`:x).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `shopify_products_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { message.error('導出失敗'); }
  };

  const items = useMemo(()=>{
    const detail = selected?.detail as any;
    const variants = (detail?.variants||[]) as any[];
    return variants.map(v=>({
      key: v.id,
      sku: v.sku,
      title: v.title,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      price: v.price,
      stock: v.inventoryQuantity,
      barcode: v.barcode,
    }));
  }, [selected]);

  const openDetail = async (record: any) => {
    try {
      const resp: any = await apiService.getShopifyProductById(record.key);
      if (resp?.success) {
        setSelected({ base: record, detail: resp.data });
      } else {
        setSelected({ base: record, detail: null });
      }
    } catch {
      setSelected({ base: record, detail: null });
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        extra={
          <Space>
            <Input placeholder="搜尋 名稱/品牌/類型" value={filters.q} onChange={(e)=>setFilters(prev=>({...prev, q:e.target.value}))} allowClear style={{ width: 280 }} />
            <Select placeholder="狀態" value={filters.status} onChange={(v)=>setFilters(prev=>({...prev, status:v}))} allowClear style={{ width: 160 }}
              options={[{label:'active', value:'active'},{label:'draft', value:'draft'},{label:'archived', value:'archived'}]} />
            <Input placeholder="品牌" value={filters.vendor} onChange={(e)=>setFilters(prev=>({...prev, vendor:e.target.value}))} allowClear style={{ width: 200 }} />
            <Button icon={<ReloadOutlined />} onClick={fetchProducts} loading={loading}>刷新</Button>
            <Button icon={<FileExcelOutlined />} onClick={handleExport} disabled={!data?.length}>導出</Button>
          </Space>
        }
      >
        <Table
          rowKey="key"
          dataSource={list}
          columns={columns as any}
          loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          onRow={(record)=>({ onClick: ()=>openDetail(record) })}
        />
      </Card>

      <Drawer title={`產品詳情 - ${selected?.base?.title||'-'}`} width={860} open={!!selected} onClose={()=>setSelected(null)}>
        {selected?.detail && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="基本資訊">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="品牌">{selected.detail.vendor||'-'}</Descriptions.Item>
                <Descriptions.Item label="類型">{selected.detail.productType||'-'}</Descriptions.Item>
                <Descriptions.Item label="狀態">{selected.detail.status||'-'}</Descriptions.Item>
                <Descriptions.Item label="Handle">{selected.detail.handle||'-'}</Descriptions.Item>
                <Descriptions.Item label="建立時間">{selected.detail.createdAt||'-'}</Descriptions.Item>
                <Descriptions.Item label="更新時間">{selected.detail.updatedAt||'-'}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Card title="變體">
              <Table
                rowKey="key"
                dataSource={items}
                pagination={false}
                columns={[
                  { title: 'SKU', dataIndex: 'sku', width: 160 },
                  { title: '名稱', dataIndex: 'title' },
                  { title: 'Option1', dataIndex: 'option1', width: 120 },
                  { title: 'Option2', dataIndex: 'option2', width: 120 },
                  { title: 'Option3', dataIndex: 'option3', width: 120 },
                  { title: '價格', dataIndex: 'price', width: 120 },
                  { title: '庫存', dataIndex: 'stock', width: 100 },
                  { title: '條碼', dataIndex: 'barcode', width: 160 },
                ]}
              />
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  );
}

export default ShopifyProduct;


